import { m } from '../i18n';
import type { MapDef, MapId } from '../world/mapdef';
import { T } from '../world/tiles';
import type { World } from '../world/world';
import { h } from './dom';
import { mapName } from '../i18n/labels';
import { closePanel, isPanelOpen, openPanel, refreshPanel } from './panel';
import { npcMarker } from '../content/story';
import { currentPin, setPin } from '../content/guide';
import { PLACES, placeOf, POI_ICON, POI_ORDER, poisOf, type Poi } from '../content/poi';
import { MAPS, getObject } from '../world/map';
import { sfx } from '../audio/sfx';

const COLORS: Record<string, string> = {
  ' ': '#120c08', '.': '#b27a47', ',': '#d6d0bc', ':': '#c0604a', ';': '#4d8f6e', k: '#cfe0e6', b: '#e6f2f5',
  '#': '#4a3424', d: '#c9a36a', D: '#ffd84a', g: '#62ab55', G: '#7cbf5a', F: '#5fac53', t: '#c4623f', p: '#d8c193',
  s: '#c9c3b6', r: '#4b4c52', l: '#4b4c52', '=': '#e6e6e0', '~': '#4f9fd6', S: '#ecd89c', h: '#2f7a3a', f: '#e8e2d4',
  R: '#c4573e', U: '#4f6fa0', Y: '#5f9a58', B: '#ece0c6', K: '#b4583e', w: '#9fd3f0', v: '#9fd3f0', A: '#2fa36a',
  X: '#cfc9bd', Q: '#b8603e', J: '#9fd3f0', O: '#6a6e78', m: '#d9d3c6', e: '#e8c44a', T: '#55585e', u: '#e6e0d0', M: '#8a8a86', q: '#3fb8c8', y: '#a86e3e', o: '#bdb8ae', Z: '#1f6fa8',
  '1': '#bdb0a0', '2': '#d4cfc5', '3': '#3a5a3a', '4': '#d9c6c0', '5': '#b85a4a', '6': '#5a5a60', '7': '#d8d2c6', '8': '#b86a4a', '9': '#4f9fd6', a: '#c9c3b6', i: '#cdc8bc', j: '#7fbf8f', x: '#bdb8ae', '!': '#c25f42', '?': '#3a5a3a',
  '@': '#cdc8bc', '$': '#beaa8c', '%': '#464850', '&': '#d9c6c0', '*': '#b85a4a', '^': '#4a3a30', '<': '#d8d2c6', '>': '#c9a36a', '|': '#5a5a60', c: '#3f7fbf', P: '#3fa8d8', n: '#5d5a58',
};

type Tab = 'here' | 'taiwan';
let tab: Tab = 'here';

/** The map panel: this map with its points of interest, or the whole of Taiwan (and Barcelona). */
export function openMap(world: World, guide: () => { x: number; y: number } | null): void {
  let raf = 0;
  openPanel(
    'map',
    () => {
      const t = m().airport.mapUi;
      const map = world.map;
      const tabs = h(
        'div',
        { class: 'journal-tabs' },
        h('button', { class: tab === 'here' ? 'on' : '', onclick: () => (sfx('click'), (tab = 'here'), refreshPanel('map')) }, t.here),
        h('button', { class: tab === 'taiwan' ? 'on' : '', onclick: () => (sfx('click'), (tab = 'taiwan'), refreshPanel('map')) }, t.taiwan),
      );
      cancelAnimationFrame(raf);
      if (tab === 'taiwan') return { title: `🗺️ ${t.taiwan}`, body: [tabs, taiwan(world)] };
      const scale = Math.min(330 / (map.w * T), 300 / (map.h * T));
      const cv = h('canvas', { class: 'minimap', width: Math.round(map.w * T * scale * 2), height: Math.round(map.h * T * scale * 2) });
      const pois = poisOf(map);
      cv.addEventListener('click', (e) => {
        const r = cv.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * map.w * T, y = ((e.clientY - r.top) / r.height) * map.h * T;
        const near = pois.map((p) => ({ p, d: Math.hypot(p.x - x, p.y - y) })).sort((a, b) => a.d - b.d)[0];
        if (near && near.d < 40) pinPoi(map.id, near.p);
      });
      const loop = () => {
        if (!isPanelOpen('map') || !cv.isConnected) return;
        paint(cv, map, scale * 2, world, guide(), pois);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      return {
        title: `🗺️ ${mapName(map.id)}`,
        body: [tabs, h('div', { class: 'minimap-wrap' }, cv), legend(map.id, pois)],
      };
    },
    () => cancelAnimationFrame(raf),
  );
}

function pinPoi(mapId: MapId, p: Poi): void {
  sfx('ok');
  setPin({ map: mapId, x: p.x, y: p.y, label: p.label });
  closePanel('map');
}

/** The tappable list of points of interest, grouped by kind. */
function legend(mapId: MapId, pois: Poi[]): HTMLElement {
  const t = m().airport.mapUi;
  const pin = currentPin();
  const groups = POI_ORDER.filter((k) => pois.some((p) => p.kind === k));
  return h(
    'div',
    { class: 'poi-list' },
    groups.map((k) =>
      h(
        'div',
        { class: 'poi-group' },
        h('small', null, `${POI_ICON[k]} ${t.kinds[k]}`),
        h(
          'div',
          { class: 'chips' },
          pois.filter((p) => p.kind === k).map((p) => h('button', { class: `chip small${pin && pin.map === mapId && pin.label === p.label ? ' on' : ''}`, onclick: () => pinPoi(mapId, p) }, `${p.icon} ${p.label}`)),
        ),
      ),
    ),
  );
}

const cache = new Map<string, HTMLCanvasElement>();

function base(map: MapDef, k: number): HTMLCanvasElement {
  const key = `${map.id}@${k.toFixed(3)}`;
  let cv = cache.get(key);
  if (cv) return cv;
  cv = document.createElement('canvas');
  cv.width = Math.round(map.w * T * k);
  cv.height = Math.round(map.h * T * k);
  const c = cv.getContext('2d')!;
  const cell = T * k;
  map.rows.forEach((row, ty) =>
    [...row].forEach((ch, tx) => {
      c.fillStyle = COLORS[ch] ?? '#8a8a8a';
      c.fillRect(Math.floor(tx * cell), Math.floor(ty * cell), Math.ceil(cell), Math.ceil(cell));
    }),
  );
  cache.set(key, cv);
  return cv;
}

function paint(cv: HTMLCanvasElement, map: MapDef, k: number, world: World, goal: { x: number; y: number } | null, pois: Poi[]): void {
  const c = cv.getContext('2d')!;
  c.drawImage(base(map, k), 0, 0);
  const t = performance.now() / 1000;
  // Points of interest: a small plate with the icon.
  const pin = currentPin();
  for (const p of pois) {
    const pinned = pin && pin.map === map.id && Math.abs(pin.x - p.x) < 1 && Math.abs(pin.y - p.y) < 1;
    c.fillStyle = pinned ? 'rgba(255,216,74,0.95)' : 'rgba(20,16,30,0.75)';
    c.beginPath();
    c.arc(p.x * k, p.y * k - 4, pinned ? 9 : 7, 0, Math.PI * 2);
    c.fill();
    c.font = `${pinned ? 11 : 9}px sans-serif`;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillText(p.icon, p.x * k, p.y * k - 3.5);
  }
  world.npcs.forEach((n, id) => {
    c.fillStyle = '#ffd84a';
    c.beginPath();
    c.arc(n.actor.x * k, n.actor.y * k, 3, 0, Math.PI * 2);
    c.fill();
    const mk = npcMarker(id);
    if (mk) {
      c.fillStyle = mk === '!' ? '#ffd84a' : '#8fd3ff';
      c.font = '900 11px sans-serif';
      c.textAlign = 'center';
      c.textBaseline = 'bottom';
      c.fillText(mk, n.actor.x * k, n.actor.y * k - 4);
    }
  });
  if (goal) {
    c.font = `${14 + Math.sin(t * 5) * 2}px sans-serif`;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillText('⭐', goal.x * k, goal.y * k - 4);
  }
  c.fillStyle = '#fff';
  c.beginPath();
  c.arc(world.player.x * k, world.player.y * k, 5, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = Math.sin(t * 8) > 0 ? '#e0533d' : '#ff8a7a';
  c.beginPath();
  c.arc(world.player.x * k, world.player.y * k, 3.5, 0, Math.PI * 2);
  c.fill();
}

// --- The Taiwan overview ---------------------------------------------------------------------

/** Rough outline of the island in [0,1] coordinates (clockwise from the north tip). */
const ISLAND: [number, number][] = [
  [0.56, 0.06], [0.64, 0.1], [0.7, 0.2], [0.72, 0.34], [0.7, 0.48], [0.66, 0.62], [0.6, 0.76], [0.52, 0.88], [0.46, 0.95], [0.4, 0.96],
  [0.34, 0.9], [0.3, 0.8], [0.28, 0.66], [0.3, 0.52], [0.34, 0.38], [0.4, 0.24], [0.46, 0.14], [0.5, 0.08],
];

function taiwan(world: World): HTMLElement {
  const t = m().airport.mapUi;
  const here = placeOf(world.map.id);
  const W = 320, H = 330;
  const cv = h('canvas', { class: 'minimap taiwan', width: W * 2, height: H * 2 });
  const c = cv.getContext('2d')!;
  c.scale(2, 2);
  // Sea, island, mountains, rail lines.
  const sea = c.createLinearGradient(0, 0, 0, H);
  sea.addColorStop(0, '#2b6fb0');
  sea.addColorStop(1, '#174a80');
  c.fillStyle = sea;
  c.fillRect(0, 0, W, H);
  c.fillStyle = 'rgba(255,255,255,0.08)';
  for (let i = 0; i < 40; i++) c.fillRect((i * 53) % W, (i * 71) % H, 10 + (i % 5) * 4, 1);
  c.fillStyle = '#7cbf5a';
  c.beginPath();
  ISLAND.forEach(([x, y], i) => (i ? c.lineTo(x * W, y * H) : c.moveTo(x * W, y * H)));
  c.closePath();
  c.fill();
  c.strokeStyle = '#3f7a3a';
  c.lineWidth = 2;
  c.stroke();
  c.fillStyle = '#4f8a3a';
  c.beginPath();
  for (const [x, y, r] of [[0.52, 0.3, 26], [0.5, 0.45, 30], [0.5, 0.6, 26], [0.48, 0.74, 18]] as const) c.ellipse(x * W, y * H, r * 0.5, r, 0, 0, Math.PI * 2);
  c.fill();
  // Rail: west coast HSR and the east line.
  c.strokeStyle = 'rgba(255,255,255,0.7)';
  c.setLineDash([3, 3]);
  c.lineWidth = 1.5;
  const at = (id: string) => PLACES.find((p) => p.id === id)!;
  const line = (ids: string[]) => {
    c.beginPath();
    ids.forEach((id, i) => (i ? c.lineTo(at(id).x * W, at(id).y * H) : c.moveTo(at(id).x * W, at(id).y * H)));
    c.stroke();
  };
  line(['town', 'taipei', 'airport']);
  line(['taipei', 'tainan', 'kaohsiung']);
  line(['town', 'hualien']);
  c.setLineDash([2, 4]);
  c.strokeStyle = 'rgba(255,216,74,0.8)';
  c.beginPath();
  c.moveTo(at('airport').x * W, at('airport').y * H);
  c.quadraticCurveTo(0.2 * W, 0.02 * H, at('barcelona').x * W, at('barcelona').y * H);
  c.stroke();
  c.setLineDash([]);
  // Barcelona inset: a little Mediterranean corner.
  c.fillStyle = 'rgba(255,255,255,0.12)';
  c.beginPath();
  c.roundRect(0.03 * W, 0.04 * H, 0.26 * W, 0.2 * H, 10);
  c.fill();
  c.fillStyle = '#d8c193';
  c.beginPath();
  c.roundRect(0.05 * W, 0.12 * H, 0.22 * W, 0.1 * H, 6);
  c.fill();
  c.font = '700 8px sans-serif';
  c.fillStyle = '#fff';
  c.textAlign = 'left';
  c.fillText(t.abroad, 0.05 * W, 0.09 * H);
  // Places.
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  for (const p of PLACES) {
    const x = p.x * W, y = p.y * H;
    const mine = p.id === here;
    c.fillStyle = mine ? '#ffd84a' : 'rgba(20,16,30,0.8)';
    c.beginPath();
    c.arc(x, y, mine ? 13 : 11, 0, Math.PI * 2);
    c.fill();
    c.font = '13px sans-serif';
    c.fillText(p.icon, x, y + 0.5);
    c.font = '800 9px sans-serif';
    c.fillStyle = '#fff';
    c.strokeStyle = 'rgba(0,0,0,0.6)';
    c.lineWidth = 3;
    c.strokeText(mapName(p.id), x, y + 20);
    c.fillText(mapName(p.id), x, y + 20);
  }
  cv.addEventListener('click', (e) => {
    const r = cv.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
    const p = PLACES.map((pl) => ({ pl, d: Math.hypot(pl.x - x, pl.y - y) })).sort((a, b) => a.d - b.d)[0];
    if (p && p.d < 0.08) {
      selected = p.pl.id;
      refreshPanel('map');
    }
  });
  const sel = PLACES.find((p) => p.id === selected) ?? PLACES.find((p) => p.id === here)!;
  const via = sel.via;
  const viaObj = via ? getObject(via.id as never) : null;
  const card = h(
    'div',
    { class: 'card place-info' },
    h('b', null, `${sel.icon} ${mapName(sel.id)}${sel.id === here ? ` · ${t.youAreHere}` : ''}`),
    h('p', { class: 'hint' }, t.how[sel.id as keyof typeof t.how] ?? ''),
    via && viaObj && sel.id !== here
      ? h(
          'button',
          {
            class: 'btn small gold',
            onclick: () => {
              sfx('ok');
              setPin({ map: via.map, x: viaObj.rect.x + viaObj.rect.w / 2, y: viaObj.rect.y + viaObj.rect.h, label: `${mapName(sel.id)} · ${m().airport.mapUi.kinds.transport}` });
              closePanel('map');
            },
          },
          t.goHere,
        )
      : null,
  );
  return h('div', null, h('div', { class: 'minimap-wrap' }, cv), card);
}

let selected: keyof typeof MAPS | null = null;
